import { merchandisingRegexError } from './regexValidation';

let conditionSequence = 0;
const nextId = () => {
  conditionSequence += 1;
  return `condition-${conditionSequence}`;
};

const decorateItem = item => {
  if (item && Array.isArray(item.items)) {
    return {
      __id: nextId(),
      match: item.match === 'any' ? 'any' : 'all',
      items: item.items.map(decorateItem),
    };
  }
  return { ...item, __id: nextId() };
};

export const decorateConditions = (
  conditions = { match: 'all', items: [] }
) => ({
  __id: 'root',
  match: conditions.match === 'any' ? 'any' : 'all',
  items: (conditions.items || []).map(decorateItem),
});

export const stripConditions = (group, extraLeafKeys = []) => ({
  match: group.match === 'any' ? 'any' : 'all',
  items: (group.items || []).map(item =>
    Array.isArray(item.items)
      ? stripConditions(item, extraLeafKeys)
      : {
          field: item.field,
          operator: item.operator,
          value: item.value,
          ...Object.fromEntries(
            extraLeafKeys
              .filter(key => item[key] !== undefined)
              .map(key => [key, item[key]])
          ),
        }
  ),
});

export const defaultConditionValue = (descriptor, operator) => {
  if (operator === 'exists') return true;
  if (descriptor?.value_type === 'boolean') return true;
  if (operator === 'between') return ['', ''];
  if (
    [
      'in',
      'not_in',
      'contains_any',
      'contains_all',
      'contains_none',
      'text_contains_any',
      'text_not_contains_any',
      'text_contains_any_case',
    ].includes(operator)
  )
    return [];
  if (descriptor?.value_type === 'number') return '';
  return '';
};

export const conditionValid = condition => {
  if (!condition?.field || !condition?.operator) return false;
  if (['matches_regex', 'not_matches_regex'].includes(condition.operator)) {
    if (
      typeof condition.value !== 'string' ||
      !condition.value.length ||
      merchandisingRegexError(condition.value)
    )
      return false;
  }
  if (Array.isArray(condition.value)) {
    if (condition.operator === 'between') {
      return (
        condition.value.length === 2 &&
        condition.value.every(
          value => value !== '' && Number.isFinite(Number(value))
        ) &&
        Number(condition.value[0]) <= Number(condition.value[1])
      );
    }
    return condition.value.length > 0;
  }
  return condition.value !== '' && condition.value != null;
};

export const conditionExpressionValid = group =>
  Array.isArray(group?.items) &&
  group.items.length > 0 &&
  group.items.every(item =>
    Array.isArray(item.items)
      ? item.items.length >= 2 && conditionExpressionValid(item)
      : conditionValid(item)
  );

const removeFrom = (group, id) => {
  let removed = null;
  const items = [];
  group.items.forEach(item => {
    if (item.__id === id) {
      removed = item;
      return;
    }
    if (Array.isArray(item.items)) {
      const nested = removeFrom(item, id);
      if (nested.removed) removed = nested.removed;
      if (nested.group.items.length === 1) items.push(nested.group.items[0]);
      else if (nested.group.items.length > 0) items.push(nested.group);
      return;
    }
    items.push(item);
  });
  return { removed, group: { ...group, items } };
};

const insertWithTarget = (group, source, targetId) => ({
  ...group,
  items: group.items.map(item => {
    if (item.__id === targetId) {
      if (Array.isArray(item.items))
        return { ...item, items: [...item.items, source] };
      return { __id: nextId(), match: 'any', items: [item, source] };
    }
    return Array.isArray(item.items)
      ? insertWithTarget(item, source, targetId)
      : item;
  }),
});

const containsIdentity = (group, id) =>
  group.__id === id ||
  group.items.some(
    item =>
      item.__id === id ||
      (Array.isArray(item.items) && containsIdentity(item, id))
  );
const withinGroupDepth = (group, depth = 1) =>
  depth <= 3 &&
  group.items.every(
    item => !Array.isArray(item.items) || withinGroupDepth(item, depth + 1)
  );

export const groupConditions = (conditions, sourceId, targetId) => {
  if (
    !sourceId ||
    sourceId === targetId ||
    !containsIdentity(conditions, targetId)
  )
    return conditions;
  const detached = removeFrom(conditions, sourceId);
  if (!detached.removed || !containsIdentity(detached.group, targetId))
    return conditions;
  const next = insertWithTarget(detached.group, detached.removed, targetId);
  return withinGroupDepth(next) ? next : conditions;
};

export const removeCondition = (conditions, id) =>
  removeFrom(conditions, id).group;

export const updateCondition = (group, id, update) => ({
  ...(group.__id === id ? { ...group, ...update } : group),
  items: group.items.map(item => {
    if (item.__id === id) return { ...item, ...update };
    return Array.isArray(item.items) ? updateCondition(item, id, update) : item;
  }),
});

export const ungroupConditions = (group, id) => ({
  ...group,
  items: group.items.flatMap(item => {
    if (item.__id === id && Array.isArray(item.items)) return item.items;
    return Array.isArray(item.items) ? [ungroupConditions(item, id)] : [item];
  }),
});

export const appendCondition = (group, condition) => ({
  ...group,
  items: [...group.items, { ...condition, __id: nextId() }],
});
